import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Table,
  Input,
  Select,
  Button,
  Space,
  Tag,
  Tooltip,
  notification,
  Drawer,
  Descriptions,
  Typography,
  Empty,
  Spin,
} from 'antd';
import { ReloadOutlined, SearchOutlined, EyeOutlined, LinkOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import lib from '../../../assets/utils/lib';
import {
  SAASPRO_DECISION_STATUS_COLORS,
  formatConfidence,
  formatCurrencyAmount,
  recommendationColor,
  recommendationLabel,
  truncateMiddle,
} from '../../../components/SaaSPro/SaaSProAdvisoryShared';

const { Text, Paragraph } = Typography;

const RECOMMENDATION_OPTIONS = [
  { value: 'all', label: 'All recommendations' },
  { value: 'approve', label: 'Approve' },
  { value: 'partial_approve', label: 'Partial approve' },
  { value: 'query', label: 'Query' },
  { value: 'reject', label: 'Reject' },
];

const STATUS_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'advisory', label: 'Advisory' },
  { value: 'applied', label: 'Applied' },
  { value: 'superseded', label: 'Superseded' },
  { value: 'rejected_by_human', label: 'Rejected by human' },
];

const fmtDateTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleString();
};

const DecisionsTab = ({ partnerId, initialFilters }) => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    status: 'all',
    recommendation: 'all',
    q: initialFilters?.q || '',
    checkin_id: initialFilters?.checkin_id || '',
    correlation_id: initialFilters?.correlation_id || '',
    claim_item_id: initialFilters?.claim_item_id || '',
  });
  const [pagination, setPagination] = useState({ page: 1, pageSize: 20, total: 0 });
  const [activeId, setActiveId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Re-apply when parent injects new filters (e.g., "See decisions for this event").
  useEffect(() => {
    if (!initialFilters) return;
    setFilters((f) => ({
      ...f,
      checkin_id: initialFilters.checkin_id ?? f.checkin_id,
      correlation_id: initialFilters.correlation_id ?? f.correlation_id,
      q: initialFilters.q ?? f.q,
      claim_item_id: initialFilters.claim_item_id ?? f.claim_item_id,
    }));
    setPagination((p) => ({ ...p, page: 1 }));
  }, [initialFilters]);

  const buildQuery = useCallback(() => {
    const q = { page: pagination.page, pageSize: pagination.pageSize };
    if (filters.status && filters.status !== 'all') q.status = filters.status;
    if (filters.recommendation && filters.recommendation !== 'all') q.recommendation = filters.recommendation;
    if (filters.q?.trim()) q.q = filters.q.trim();
    if (filters.checkin_id?.trim()) q.checkin_id = filters.checkin_id.trim();
    if (filters.correlation_id?.trim()) q.correlation_id = filters.correlation_id.trim();
    if (filters.claim_item_id?.trim()) q.claim_item_id = filters.claim_item_id.trim();
    return q;
  }, [filters, pagination.page, pagination.pageSize]);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await lib.getPaAgentModuleDecisions(partnerId, buildQuery());
      const body = resp?.data || resp;
      const data = body?.data || body;
      const list = Array.isArray(data?.decisions)
        ? data.decisions
        : Array.isArray(data?.rows)
          ? data.rows
          : Array.isArray(data)
            ? data
            : [];
      const pag = body?.pagination || data?.pagination || {};
      const total = Number(pag.total ?? data?.total ?? list.length) || 0;
      setRows(list);
      setPagination((p) => ({ ...p, total }));
    } catch (e) {
      notification.error({ message: 'Failed to load decisions', description: e?.message });
    } finally {
      setLoading(false);
    }
  }, [buildQuery, partnerId]);

  useEffect(() => {
    if (!partnerId) return;
    fetchRows();
  }, [fetchRows, partnerId]);

  const openDetail = useCallback(async (row) => {
    const id = row?.id ?? row?.correlation_id;
    if (id == null) return;
    setActiveId(id);
    setDetail(null);
    setDetailLoading(true);
    try {
      const resp = await lib.getPaAgentDecisionDetail(partnerId, id);
      const body = resp?.data || resp;
      if (Number(body?.error) || resp?.status === 'error') {
        notification.error({ message: body?.message || 'Could not load decision' });
        setActiveId(null);
        return;
      }
      setDetail(body?.data || body);
    } catch (e) {
      notification.error({ message: 'Could not load decision', description: e?.message });
      setActiveId(null);
    } finally {
      setDetailLoading(false);
    }
  }, [partnerId]);

  const closeDetail = useCallback(() => {
    setActiveId(null);
    setDetail(null);
  }, []);

  const columns = useMemo(() => [
    {
      title: 'Received',
      dataIndex: 'received_at',
      width: 170,
      sorter: (a, b) => new Date(a.received_at) - new Date(b.received_at),
      defaultSortOrder: 'descend',
      render: (v) => <span style={{ fontSize: 12 }}>{fmtDateTime(v)}</span>,
    },
    {
      title: 'Decided',
      dataIndex: 'decided_at',
      width: 170,
      render: (v) => <span style={{ fontSize: 12 }}>{fmtDateTime(v)}</span>,
    },
    {
      title: 'Checkin id',
      dataIndex: 'checkin_id',
      width: 160,
      render: (v) => (
        <Button
          type="link"
          size="small"
          icon={<LinkOutlined />}
          style={{ padding: 0 }}
          onClick={(e) => { e.stopPropagation(); navigate(`/preauth-action?checkin=${encodeURIComponent(v)}`, { state: { checkinId: v } }); }}
        >
          {v}
        </Button>
      ),
    },
    {
      title: 'Item',
      dataIndex: 'claim_item_id',
      width: 80,
      render: (v) => v ?? '--',
    },
    {
      title: 'Recommendation',
      dataIndex: 'recommendation',
      width: 160,
      render: (v) => (
        <Tag color={recommendationColor(v)} style={{ marginInlineEnd: 0, fontWeight: 600 }}>
          {recommendationLabel(v)}
        </Tag>
      ),
    },
    {
      title: 'Recommended cost',
      dataIndex: 'recommended_approved_cost',
      width: 140,
      align: 'right',
      render: (v) => formatCurrencyAmount(v, '--'),
    },
    {
      title: 'Confidence',
      dataIndex: 'confidence',
      width: 110,
      align: 'right',
      render: (v) => formatConfidence(v) || '--',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      width: 130,
      render: (v) => (
        <Tag color={SAASPRO_DECISION_STATUS_COLORS[v] || 'default'} style={{ marginInlineEnd: 0 }}>
          {v}
        </Tag>
      ),
    },
    {
      title: 'Correlation',
      dataIndex: 'correlation_id',
      width: 140,
      render: (v) => (
        <Tooltip title={v}>
          <code style={{ fontSize: 12 }}>{truncateMiddle(v, 5, 5)}</code>
        </Tooltip>
      ),
    },
    {
      title: '',
      key: 'actions',
      width: 56,
      render: (_, row) => (
        <Button
          size="small"
          icon={<EyeOutlined />}
          onClick={(e) => { e.stopPropagation(); openDetail(row); }}
        />
      ),
    },
  ], [navigate, openDetail]);

  const rawPayloadString = useMemo(() => {
    if (!detail?.raw_payload) return '';
    try {
      return JSON.stringify(detail.raw_payload, null, 2);
    } catch (e) {
      return String(detail.raw_payload);
    }
  }, [detail]);

  const citations = useMemo(() => {
    if (!detail) return [];
    return Array.isArray(detail.policy_citations) ? detail.policy_citations : [];
  }, [detail]);

  return (
    <div>
      <Space wrap style={{ marginBottom: 12 }}>
        <Input
          allowClear
          prefix={<SearchOutlined />}
          placeholder="Search checkin, correlation, item, rationale…"
          style={{ width: 300 }}
          value={filters.q}
          onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
          onPressEnter={() => { setPagination((p) => ({ ...p, page: 1 })); fetchRows(); }}
        />
        <Select
          style={{ width: 170 }}
          value={filters.status}
          onChange={(v) => setFilters((f) => ({ ...f, status: v }))}
          options={STATUS_OPTIONS}
        />
        <Select
          style={{ width: 180 }}
          value={filters.recommendation}
          onChange={(v) => setFilters((f) => ({ ...f, recommendation: v }))}
          options={RECOMMENDATION_OPTIONS}
        />
        <Input
          allowClear
          placeholder="Checkin id"
          style={{ width: 200 }}
          value={filters.checkin_id}
          onChange={(e) => setFilters((f) => ({ ...f, checkin_id: e.target.value }))}
          onPressEnter={() => { setPagination((p) => ({ ...p, page: 1 })); fetchRows(); }}
        />
        <Input
          allowClear
          placeholder="Correlation id"
          style={{ width: 220 }}
          value={filters.correlation_id}
          onChange={(e) => setFilters((f) => ({ ...f, correlation_id: e.target.value }))}
          onPressEnter={() => { setPagination((p) => ({ ...p, page: 1 })); fetchRows(); }}
        />
        <Input
          allowClear
          placeholder="Claim item id"
          style={{ width: 140 }}
          value={filters.claim_item_id}
          onChange={(e) => setFilters((f) => ({ ...f, claim_item_id: e.target.value }))}
          onPressEnter={() => { setPagination((p) => ({ ...p, page: 1 })); fetchRows(); }}
        />
        <Button
          type="primary"
          onClick={() => { setPagination((p) => ({ ...p, page: 1 })); fetchRows(); }}
        >
          Apply
        </Button>
        <Button icon={<ReloadOutlined />} onClick={fetchRows}>Refresh</Button>
      </Space>

      <Table
        size="small"
        rowKey={(row) => row?.id ?? row?.correlation_id ?? `${row?.checkin_id}-${row?.claim_item_id}`}
        columns={columns}
        dataSource={rows}
        loading={loading}
        onRow={(row) => ({ onClick: () => openDetail(row) })}
        pagination={{
          current: pagination.page,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true,
          onChange: (page, pageSize) => setPagination({ ...pagination, page, pageSize }),
        }}
        locale={{
          emptyText: (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                <span style={{ color: 'rgba(0,0,0,0.55)' }}>
                  No advisory decisions yet for these filters.
                </span>
              }
            />
          ),
        }}
      />

      <Drawer
        title={detail ? (
          <Space>
            <Tag color={recommendationColor(detail.recommendation)}>{recommendationLabel(detail.recommendation)}</Tag>
            <Text>{detail.checkin_id}</Text>
            <code style={{ fontSize: 12 }}>item {detail.claim_item_id ?? '--'}</code>
          </Space>
        ) : 'Decision'}
        width={680}
        open={Boolean(activeId)}
        onClose={closeDetail}
        destroyOnClose
      >
        {detailLoading ? (
          <Spin />
        ) : detail ? (
          <>
            <Descriptions size="small" column={1} bordered>
              <Descriptions.Item label="Status">
                <Tag color={SAASPRO_DECISION_STATUS_COLORS[detail.status] || 'default'}>{detail.status}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Recommended cost">
                {formatCurrencyAmount(detail.recommended_approved_cost, '--')}
              </Descriptions.Item>
              <Descriptions.Item label="Confidence">
                {formatConfidence(detail.confidence) || '--'}
              </Descriptions.Item>
              <Descriptions.Item label="Correlation id">
                <code style={{ fontSize: 12 }}>{detail.correlation_id}</code>
              </Descriptions.Item>
              <Descriptions.Item label="Event id">
                <code style={{ fontSize: 12 }}>{detail.event_id || '--'}</code>
              </Descriptions.Item>
              <Descriptions.Item label="Decided at">{fmtDateTime(detail.decided_at)}</Descriptions.Item>
              <Descriptions.Item label="Received at">{fmtDateTime(detail.received_at)}</Descriptions.Item>
              <Descriptions.Item label="Overall">
                {detail.overall_recommendation ? (
                  <Tag color={recommendationColor(detail.overall_recommendation)}>
                    {recommendationLabel(detail.overall_recommendation)}
                  </Tag>
                ) : '--'}
              </Descriptions.Item>
            </Descriptions>

            {detail.rationale && (
              <div style={{ marginTop: 16 }}>
                <Text strong>Rationale</Text>
                <Paragraph
                  style={{
                    background: 'rgba(0,0,0,0.03)',
                    border: '1px solid rgba(0,0,0,0.06)',
                    borderRadius: 6,
                    padding: 10,
                    marginTop: 6,
                    whiteSpace: 'pre-wrap',
                  }}
                >
                  {detail.rationale}
                </Paragraph>
              </div>
            )}

            {detail.overall_rationale && (
              <div style={{ marginTop: 8 }}>
                <Text strong>Overall rationale</Text>
                <Paragraph
                  style={{
                    background: 'rgba(0,0,0,0.03)',
                    border: '1px solid rgba(0,0,0,0.06)',
                    borderRadius: 6,
                    padding: 10,
                    marginTop: 6,
                    whiteSpace: 'pre-wrap',
                  }}
                >
                  {detail.overall_rationale}
                </Paragraph>
              </div>
            )}

            {citations.length > 0 && (
              <div style={{ marginTop: 12 }}>
                <Text strong>Citations</Text>
                <div style={{ marginTop: 6 }}>
                  {citations.map((cite, idx) => {
                    const isUrl = typeof cite === 'string' && /^https?:\/\//i.test(cite);
                    return (
                      <Tag key={`${cite}-${idx}`} color="geekblue" style={{ marginBottom: 4 }}>
                        {isUrl ? (
                          <a href={cite} target="_blank" rel="noreferrer">{cite}</a>
                        ) : cite}
                      </Tag>
                    );
                  })}
                </div>
              </div>
            )}

            <div style={{ marginTop: 16 }}>
              <Text strong>Raw payload</Text>
              <Paragraph
                style={{
                  background: '#0b1021',
                  color: '#dbe4ff',
                  borderRadius: 6,
                  padding: 12,
                  marginTop: 6,
                  maxHeight: 320,
                  overflow: 'auto',
                  fontFamily: 'ui-monospace,Menlo,Consolas,monospace',
                  fontSize: 12,
                  whiteSpace: 'pre',
                }}
              >
                {rawPayloadString || '(no raw payload)'}
              </Paragraph>
            </div>
          </>
        ) : null}
      </Drawer>
    </div>
  );
};

export default DecisionsTab;
